import { useState, useRef, useCallback, useEffect } from "react";
import type { UIMessage } from "ai";
import type { TTSWorkerResponse, VoiceState } from "./types";
import { AudioPlayer } from "./audio-player";
import { AudioCache } from "./audio-cache";
import { DEFAULT_VOICE_ID } from "./voices";
import { stripMarkdown } from "../../shared/strip-markdown.js";
import { audioDebug } from "./debug-overlay";

// Cap TTS text to prevent memory exhaustion from very long messages (~25 min of speech)
const MAX_TTS_CHARS = 50_000;

function extractTextFromMessage(message: UIMessage): string {
  const textParts = message.parts
    .filter((p): p is Extract<typeof p, { type: "text" }> => p.type === "text")
    .map((p) => p.text);
  return stripMarkdown(textParts.join("")).slice(0, MAX_TTS_CHARS);
}

interface UseVoiceOptions {
  voiceEnabled: boolean;
  voiceId: string;
  autoSpeak: boolean;
  playbackSpeed?: number;
  // Server TTS
  ttsMode?: "browser" | "server";
  sendTTSRequest?: (messageId: string, text: string, voiceId: string, requestId: string) => void;
  sendTTSStop?: () => void;
}

interface UseVoiceReturn {
  voiceState: VoiceState;
  speakingMessageId: string | null;
  initVoice: () => void;
  speakMessage: (message: UIMessage) => void;
  stopSpeaking: () => void;
  seekTo: (messageId: string, fraction: number) => void;
  getPlaybackProgress: () => number;
  getAudioLevel: () => number;
  audioCache: AudioCache;
  restartVoice: () => void;
  playbackSpeed: number;
  // Callbacks for useChat to wire binary frames into — only used in server TTS mode
  onServerAudioChunk: (data: ArrayBuffer) => void;
  onTTSStart: (messageId: string, sampleRate: number, voiceId: string, sourceId?: string) => void;
  onTTSEnd: (messageId: string, sourceId?: string) => void;
}

export function useVoice(
  messages: UIMessage[],
  status: string,
  options: UseVoiceOptions,
): UseVoiceReturn {
  const [voiceState, setVoiceState] = useState<VoiceState>("unloaded");
  const downloadProgressRef = useRef(0);
  const deviceTypeRef = useRef<"webgpu" | "wasm" | null>(null);
  const [speakingMessageId, setSpeakingMessageId] = useState<string | null>(null);

  const workerRef = useRef<Worker | null>(null);
  const playerRef = useRef<AudioPlayer>(new AudioPlayer());
  const cacheRef = useRef<AudioCache>(new AudioCache());
  const voiceIdRef = useRef(options.voiceId || DEFAULT_VOICE_ID);
  const drainIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pendingMessageRef = useRef<UIMessage | null>(null);
  // Ref mirror of speakingMessageId — used for toggle check in speakMessage to avoid
  // stale closure reads when state hasn't propagated between rapid click events.
  const speakingMessageIdRef = useRef<string | null>(null);
  // Browser/cached manual replay guard. Server-stream routing is bound separately
  // by message + generation so a stale replay cannot suppress current audio.
  const isManualReplayRef = useRef(false);
  const serverTtsSourceRef = useRef<{
    generation: number;
    kind: "none" | "cache" | "request" | "auto";
    requestedMessageId: string | null;
    sourceId: string | null;
    activeMessageId: string | null;
  }>({ generation: 0, kind: "none", requestedMessageId: null, sourceId: null, activeMessageId: null });
  const replaceServerTtsSource = useCallback((
    kind: "none" | "cache" | "request" | "auto",
    requestedMessageId: string | null = null,
    sourceId: string | null = null,
  ) => {
    const generation = serverTtsSourceRef.current.generation + 1;
    serverTtsSourceRef.current = { generation, kind, requestedMessageId, sourceId, activeMessageId: null };
    return generation;
  }, []);
  // Refs for auto-speak missed message on model ready (Bug 5)
  const autoSpeakRef = useRef(options.autoSpeak);
  const voiceEnabledRef = useRef(options.voiceEnabled);
  const messagesRef = useRef(messages);

  useEffect(() => { autoSpeakRef.current = options.autoSpeak; }, [options.autoSpeak]);
  useEffect(() => { voiceEnabledRef.current = options.voiceEnabled; }, [options.voiceEnabled]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // Server TTS option refs — mirror props so callbacks stay stable
  const ttsModeRef = useRef(options.ttsMode ?? "browser");
  const sendTTSRequestRef = useRef(options.sendTTSRequest);
  const sendTTSStopRef = useRef(options.sendTTSStop);
  const playbackSpeedRef = useRef(options.playbackSpeed ?? 1.0);
  useEffect(() => { ttsModeRef.current = options.ttsMode ?? "browser"; }, [options.ttsMode]);
  useEffect(() => { sendTTSRequestRef.current = options.sendTTSRequest; }, [options.sendTTSRequest]);
  useEffect(() => { sendTTSStopRef.current = options.sendTTSStop; }, [options.sendTTSStop]);
  useEffect(() => { playbackSpeedRef.current = options.playbackSpeed ?? 1.0; }, [options.playbackSpeed]);

  // Always update both state and ref together so toggle checks never read stale values
  const updateSpeakingId = useCallback((id: string | null) => {
    speakingMessageIdRef.current = id;
    setSpeakingMessageId(id);
  }, []);

  // Keep voiceId ref in sync — and force-stop any active playback/streaming
  // so the next generation uses the new voice.
  useEffect(() => {
    const prev = voiceIdRef.current;
    voiceIdRef.current = options.voiceId || DEFAULT_VOICE_ID;

    if (prev !== voiceIdRef.current) {
      // Voice changed — kill current playback so next gen uses the new voice
      isManualReplayRef.current = false;
      replaceServerTtsSource("none");
      playerRef.current.stop();
      workerRef.current?.postMessage({ type: "stop" });
      updateSpeakingId(null);
    }
  }, [options.voiceId, updateSpeakingId, replaceServerTtsSource]);

  // Both TTS modes now generate audio natively at the requested speed
  // (browser: Kokoro speed param; server: gateway speed param), so playbackRate
  // must stay at 1.0 in all cases — setting it above 1.0 would double-apply
  // the speed and shift pitch upward (Mickey Mouse effect).
  useEffect(() => {
    playerRef.current.playbackRate = 1.0;
  }, [options.playbackSpeed, options.ttsMode]);

  // Drain polling extracted into a reusable helper — used by both generation-done and cache replay
  const startDrainPolling = useCallback((forId: string, serverGeneration?: number) => {
    if (drainIntervalRef.current) clearInterval(drainIntervalRef.current);
    drainIntervalRef.current = setInterval(() => {
      if (!playerRef.current.isPlaying) {
        const sourceStillOwnsDrain = serverGeneration === undefined
          || serverTtsSourceRef.current.generation === serverGeneration;
        // Both message and server generation must still match. This protects a
        // rapid replay of the same message from an older drain poll.
        if (speakingMessageIdRef.current === forId && sourceStillOwnsDrain) {
          isManualReplayRef.current = false;
          replaceServerTtsSource("none");
          setVoiceState("ready");
          updateSpeakingId(null);
        }
        if (drainIntervalRef.current) {
          clearInterval(drainIntervalRef.current);
          drainIntervalRef.current = null;
        }
      }
    }, 200);
  }, [updateSpeakingId, replaceServerTtsSource]);

  const getOrCreateWorker = useCallback(() => {
    if (workerRef.current) return workerRef.current;

    let worker: Worker;
    try {
      worker = new Worker(
        new URL("./tts.worker.ts", import.meta.url),
        { type: "module" },
      );
    } catch (err) {
      console.error("[voice] Failed to create worker:", err);
      setVoiceState("error");
      return null;
    }

    worker.onerror = (err) => {
      console.error("[voice] Worker failed to load:", err);
      setVoiceState("error");
    };

    worker.onmessage = (e: MessageEvent<TTSWorkerResponse>) => {
      const msg = e.data;

      switch (msg.type) {
        case "init-progress":
          downloadProgressRef.current = msg.progress;
          break;

        case "ready":
          setVoiceState("ready");
          deviceTypeRef.current = msg.deviceType;
          downloadProgressRef.current = 100;
          // Speak queued message if user clicked speaker while model was loading
          if (pendingMessageRef.current) {
            const pending = pendingMessageRef.current;
            pendingMessageRef.current = null;
            const text = extractTextFromMessage(pending);
            if (text) {
              playerRef.current.prime();
              updateSpeakingId(pending.id);
              workerRef.current?.postMessage({ type: "generate", text, voiceId: voiceIdRef.current, speed: playbackSpeedRef.current });
            }
          } else if (autoSpeakRef.current && voiceEnabledRef.current) {
            // Auto-speak missed message — model finished loading after stream already completed
            const msgs = messagesRef.current;
            const lastMsg = msgs[msgs.length - 1];
            if (lastMsg && lastMsg.role === "assistant") {
              const text = extractTextFromMessage(lastMsg);
              if (text) {
                playerRef.current.prime();
                updateSpeakingId(lastMsg.id);
                workerRef.current?.postMessage({ type: "generate", text, voiceId: voiceIdRef.current, speed: playbackSpeedRef.current });
              }
            }
          }
          break;

        case "audio-chunk": {
          // Skip streaming chunks that arrive after manual replay started
          if (isManualReplayRef.current) break;
          // Cache the chunk before enqueueing for playback
          const cacheKey = speakingMessageIdRef.current
            ? AudioCache.makeKey(speakingMessageIdRef.current, voiceIdRef.current)
            : null;
          if (cacheKey) {
            cacheRef.current.append(cacheKey, msg.samples, msg.sampleRate);
          }
          playerRef.current.enqueue(msg.samples, msg.sampleRate);
          setVoiceState("speaking");
          break;
        }

        case "generation-done": {
          // Mark cache entry complete so replays know all chunks are in
          const doneCacheKey = speakingMessageIdRef.current
            ? AudioCache.makeKey(speakingMessageIdRef.current, voiceIdRef.current)
            : null;
          if (doneCacheKey) {
            cacheRef.current.markComplete(doneCacheKey);
          }
          // Poll until audio drains — onended fires per-source, not globally
          startDrainPolling(speakingMessageIdRef.current ?? "");
          break;
        }

        case "busy":
          // Worker is mid-stream; manual replay request was dropped — reset UI cleanly
          isManualReplayRef.current = false;
          updateSpeakingId(null);
          setVoiceState(prev => prev === "speaking" ? "ready" : prev);
          break;

        case "error":
          console.error("[voice] Worker error:", msg.message);
          setVoiceState("error");
          updateSpeakingId(null);
          break;
      }
    };

    workerRef.current = worker;
    return worker;
  }, [startDrainPolling, updateSpeakingId]);

  const initVoice = useCallback(() => {
    if (voiceState === "loading" || voiceState === "ready") {
      return;
    }
    setVoiceState("loading");
    downloadProgressRef.current = 0;
    // Prime AudioContext during user gesture — avoids autoplay policy issues later
    playerRef.current.prime();
    const worker = getOrCreateWorker();
    if (!worker) return;
    worker.postMessage({ type: "init", voiceId: voiceIdRef.current });
  }, [voiceState, getOrCreateWorker]);

  const stopSpeaking = useCallback(() => {
    isManualReplayRef.current = false;
    replaceServerTtsSource("none");
    if (drainIntervalRef.current) {
      clearInterval(drainIntervalRef.current);
      drainIntervalRef.current = null;
    }
    playerRef.current.stop();
    if (ttsModeRef.current === "server") {
      sendTTSStopRef.current?.();
    } else {
      const worker = workerRef.current;
      if (worker) worker.postMessage({ type: "stop" });
    }
    updateSpeakingId(null);
    setVoiceState(prev => prev === "speaking" ? "ready" : prev);
  }, [updateSpeakingId, replaceServerTtsSource]);

  const speakMessage = useCallback(
    (message: UIMessage) => {
      // Toggle off if already speaking this message — read ref to avoid stale closure
      if (speakingMessageIdRef.current === message.id) {
        stopSpeaking();
        return;
      }

      const text = extractTextFromMessage(message);
      if (!text) {
        return;
      }

      // Stop any active auto-speak stream before starting manual playback so
      // in-flight chunks don't collide with the replay audio in AudioPlayer.
      if (isStreamingRef.current) {
        isStreamingRef.current = false;
        streamVoiceIdRef.current = null;
        if (ttsModeRef.current === "server") {
          sendTTSStopRef.current?.();
        } else {
          workerRef.current?.postMessage({ type: "stop" });
        }
      }

      // ─── Server TTS path ───────────────────────────────────────────────────
      if (ttsModeRef.current === "server") {
        // Check cache first — server audio was cached the same as browser audio
        const cacheKey = AudioCache.makeKey(message.id, voiceIdRef.current);
        if (cacheRef.current.has(cacheKey)) {
          const cached = cacheRef.current.getMerged(cacheKey);
          if (cached) {
            isManualReplayRef.current = true;
            const serverGeneration = replaceServerTtsSource("cache", message.id);
            if (drainIntervalRef.current) {
              clearInterval(drainIntervalRef.current);
              drainIntervalRef.current = null;
            }
            playerRef.current.stop();
            playerRef.current.prime();
            updateSpeakingId(message.id);
            playerRef.current.playFromBuffer(cached.samples, cached.sampleRate);
            setVoiceState("speaking");
            startDrainPolling(message.id, serverGeneration);
            return;
          }
        }

        // No cache — replace any cached/manual drain with an explicit server
        // request generation. `tts_start(messageId, sourceId)` is the authority
        // that binds subsequent binary frames to this accepted request.
        isManualReplayRef.current = false;
        const requestId = `tts_${crypto.randomUUID()}`;
        replaceServerTtsSource("request", message.id, requestId);
        if (drainIntervalRef.current) {
          clearInterval(drainIntervalRef.current);
          drainIntervalRef.current = null;
        }
        playerRef.current.stop();
        playerRef.current.prime();
        updateSpeakingId(message.id);
        sendTTSRequestRef.current?.(message.id, text, voiceIdRef.current, requestId);
        return;
      }

      // ─── Browser TTS path (unchanged) ─────────────────────────────────────

      // Queue if model still loading — will auto-speak when ready
      if (voiceState === "loading") {
        pendingMessageRef.current = message;
        updateSpeakingId(message.id);
        return;
      }

      // Not ready and not loading — trigger init, queue, and bail
      if (voiceState !== "ready" && voiceState !== "speaking") {
        pendingMessageRef.current = message;
        updateSpeakingId(message.id);
        initVoice();
        return;
      }

      // Check cache for instant replay — skip TTS generation entirely
      const cacheKey = AudioCache.makeKey(message.id, voiceIdRef.current);
      if (cacheRef.current.has(cacheKey)) {
        const cached = cacheRef.current.getMerged(cacheKey);
        if (cached) {
          isManualReplayRef.current = true;
          playerRef.current.stop();
          playerRef.current.prime();
          updateSpeakingId(message.id);
          playerRef.current.playFromBuffer(cached.samples, cached.sampleRate);
          setVoiceState("speaking");
          startDrainPolling(message.id);
          return;
        }
      }

      // Stop whatever's currently playing, prime AudioContext during user gesture
      isManualReplayRef.current = true;
      playerRef.current.stop();
      playerRef.current.prime();
      updateSpeakingId(message.id);

      const worker = getOrCreateWorker();
      if (!worker) return;
      worker.postMessage({ type: "generate", text, voiceId: voiceIdRef.current, speed: playbackSpeedRef.current });
    },
    [voiceState, initVoice, stopSpeaking, getOrCreateWorker, updateSpeakingId, startDrainPolling, replaceServerTtsSource],
  );

  // Seek to a fraction (0–1) within a cached message's audio
  const seekTo = useCallback((messageId: string, fraction: number) => {
    const cacheKey = AudioCache.makeKey(messageId, voiceIdRef.current);
    const cached = cacheRef.current.getMerged(cacheKey);
    if (!cached) return;

    const offsetSamples = Math.floor(fraction * cached.samples.length);
    playerRef.current.stop();
    playerRef.current.prime();
    updateSpeakingId(messageId);
    isManualReplayRef.current = true;
    playerRef.current.playFromBuffer(cached.samples, cached.sampleRate, offsetSamples);
    setVoiceState("speaking");
    startDrainPolling(messageId);
  }, [updateSpeakingId, startDrainPolling]);

  const getPlaybackProgress = useCallback(() => {
    return playerRef.current.getPlaybackProgress();
  }, []);

  // Hard reset + re-init — useful when voice gets stuck in a bad state
  const restartVoice = useCallback(() => {
    // Tear down
    isManualReplayRef.current = false;
    if (drainIntervalRef.current) {
      clearInterval(drainIntervalRef.current);
      drainIntervalRef.current = null;
    }
    playerRef.current.stop();
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
    updateSpeakingId(null);
    pendingMessageRef.current = null;
    // Re-init immediately — set loading (not unloaded) to skip the guard in initVoice
    setVoiceState("loading");
    downloadProgressRef.current = 0;
    deviceTypeRef.current = null;
    playerRef.current.prime();
    const worker = getOrCreateWorker();
    if (worker) {
      worker.postMessage({ type: "init", voiceId: voiceIdRef.current });
    }
  }, [updateSpeakingId, getOrCreateWorker]);

  // --- Streaming TTS (auto-speak during LLM response) ---
  const streamedLengthRef = useRef(0);
  const isStreamingRef = useRef(false);
  const streamVoiceIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Server TTS mode: gateway handles streaming audio; don't duplicate client-side
    if (ttsModeRef.current === "server") return;
    // Manual replay in progress — don't let auto-speak interrupt it
    if (isManualReplayRef.current) return;

    if (!options.voiceEnabled || !options.autoSpeak) return;
    if (voiceState !== "ready" && voiceState !== "speaking") return;

    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || lastMessage.role !== "assistant") return;

    const currentText = extractTextFromMessage(lastMessage);

    if (status === "streaming") {
      const currentVoice = voiceIdRef.current;

      // Restart stream if voice changed mid-stream
      if (isStreamingRef.current && streamVoiceIdRef.current !== currentVoice) {
        const worker = workerRef.current;
        if (worker) {
          worker.postMessage({ type: "stop" });
          playerRef.current.stop();
        }
        isStreamingRef.current = false;
      }

      if (!isStreamingRef.current) {
        // Stream just started (or restarted after voice change)
        isStreamingRef.current = true;
        streamVoiceIdRef.current = currentVoice;
        streamedLengthRef.current = 0;
        playerRef.current.stop();
        playerRef.current.prime();
        updateSpeakingId(lastMessage.id);
        const worker = getOrCreateWorker();
        if (!worker) return;
        worker.postMessage({ type: "stream-start", voiceId: currentVoice, speed: playbackSpeedRef.current });
      }

      // Push delta (capped to prevent memory exhaustion)
      if (streamedLengthRef.current >= MAX_TTS_CHARS) return;
      const delta = currentText.slice(streamedLengthRef.current, MAX_TTS_CHARS);
      if (delta.length > 0) {
        streamedLengthRef.current = Math.min(currentText.length, MAX_TTS_CHARS);
        const worker = getOrCreateWorker();
        if (!worker) return;
        worker.postMessage({ type: "stream-push", text: delta });
      }
    } else if (isStreamingRef.current && (status === "idle" || status === "ready")) {
      // Stream ended — close the splitter
      isStreamingRef.current = false;
      streamVoiceIdRef.current = null;
      const worker = workerRef.current;
      if (worker) worker.postMessage({ type: "stream-end" });
    }
  }, [messages, status, options.voiceEnabled, options.autoSpeak, voiceState, getOrCreateWorker, updateSpeakingId]);

  // Reset streaming state and cache on session switch (messages array cleared)
  useEffect(() => {
    if (messages.length === 0) {
      isStreamingRef.current = false;
      streamedLengthRef.current = 0;
      streamVoiceIdRef.current = null;
      cacheRef.current.clear();
    }
  }, [messages.length]);

  // Tab visibility: intentionally NO pause/resume.
  // Suspending AudioContext on tab switch kills background playback and
  // desynchronizes scheduled sources (causing replay cutoff). Browsers
  // handle resource management for background tabs on their own.

  // --- Web Speech API fallback on worker init error ---
  const speakFallback = useCallback((text: string) => {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.onend = () => {
      setVoiceState("error"); // stay in error — fallback is degraded
      updateSpeakingId(null);
    };
    window.speechSynthesis.speak(utterance);
  }, [updateSpeakingId]);

  // --- Server TTS callbacks ---
  // These are wired into useChat's refs so binary audio frames route here.

  // Stores the sample rate received in tts_start so onServerAudioChunk uses the real value
  const serverSampleRateRef = useRef(24000);

  const onServerAudioChunk = useCallback((data: ArrayBuffer) => {
    if (ttsModeRef.current !== "server") return;
    const source = serverTtsSourceRef.current;
    if (!source.activeMessageId || source.activeMessageId !== speakingMessageIdRef.current) return;
    const samples = new Float32Array(data);
    audioDebug('chunk', `received ${samples.length} samples`);
    // Cache the chunk under the speaking message ID
    const cacheKey = speakingMessageIdRef.current
      ? AudioCache.makeKey(speakingMessageIdRef.current, voiceIdRef.current)
      : null;
    if (cacheKey) {
      cacheRef.current.append(cacheKey, samples, serverSampleRateRef.current);
    }
    playerRef.current.enqueue(samples, serverSampleRateRef.current);
    setVoiceState("speaking");
  }, []);

  const onTTSStart = useCallback((messageId: string, sampleRate: number, _voiceId: string, sourceId?: string) => {
    if (ttsModeRef.current !== "server") return;
    const source = serverTtsSourceRef.current;
    if (source.kind === "cache") return;
    if (source.kind === "request") {
      if (source.requestedMessageId !== messageId || source.sourceId !== (sourceId ?? null)) return;
      serverTtsSourceRef.current = { ...source, activeMessageId: messageId };
    } else {
      // Gateway-assigned sourceId distinguishes even same-message auto generations.
      const generation = replaceServerTtsSource("auto", messageId);
      serverTtsSourceRef.current = {
        generation, kind: "auto", requestedMessageId: messageId, sourceId: sourceId ?? null, activeMessageId: messageId,
      };
    }
    audioDebug('tts', `▶ tts_start msgId=${messageId.slice(0,8)} rate=${sampleRate}`);
    // Store actual sample rate so subsequent audio chunks use the correct rate
    serverSampleRateRef.current = sampleRate;
    // stop() resets sources without closing the AudioContext — always safe to call
    playerRef.current.stop();
    // Only call prime() when context doesn't already exist or isn't running.
    // On Android Chrome, ctx.resume() on a suspended context triggers an OS audio
    // session acquisition sound (the beep). If the context is already running,
    // prime() is a no-op and skipping it avoids the spurious resume() call.
    const shouldPrime = !playerRef.current.isContextRunning();
    console.log(`[HEALTH:tts-start] prime=${shouldPrime} ctxState=${playerRef.current.ctxState()} messageId=${messageId}`);
    if (shouldPrime) {
      playerRef.current.prime();
    }
    updateSpeakingId(messageId);
    setVoiceState("speaking");
  }, [updateSpeakingId, replaceServerTtsSource]);

  const onTTSEnd = useCallback((messageId: string, sourceId?: string) => {
    if (ttsModeRef.current !== "server") return;
    const source = serverTtsSourceRef.current;
    const tokenMatches = source.sourceId === (sourceId ?? null);
    const ownsCurrentRequest = source.kind === "request" && source.requestedMessageId === messageId && tokenMatches;
    const ownsActiveStream = source.activeMessageId === messageId && tokenMatches;
    if ((!ownsCurrentRequest && !ownsActiveStream) || speakingMessageIdRef.current !== messageId) return;
    audioDebug('tts', `■ tts_end msgId=${messageId.slice(0,8)}`);
    // Mark cache complete
    const cacheKey = AudioCache.makeKey(messageId, voiceIdRef.current);
    cacheRef.current.markComplete(cacheKey);
    // Start drain polling — same as browser TTS path
    startDrainPolling(messageId, source.generation);
  }, [startDrainPolling]);

  // Override speakMessage to fall back on error state
  const speakMessageWithFallback = useCallback(
    (message: UIMessage) => {
      if (voiceState === "error") {
        if (speakingMessageIdRef.current === message.id) {
          window.speechSynthesis?.cancel();
          updateSpeakingId(null);
          return;
        }
        const text = extractTextFromMessage(message);
        if (!text) return;
        updateSpeakingId(message.id);
        speakFallback(text);
        return;
      }
      speakMessage(message);
    },
    [voiceState, speakMessage, speakFallback, updateSpeakingId],
  );

  const getAudioLevel = useCallback(() => {
    return playerRef.current.getAudioLevel();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      workerRef.current?.terminate();
      playerRef.current.destroy();
      if (drainIntervalRef.current) clearInterval(drainIntervalRef.current);
    };
  }, []);

  return {
    voiceState,
    speakingMessageId,
    initVoice,
    speakMessage: speakMessageWithFallback,
    stopSpeaking,
    seekTo,
    getPlaybackProgress,
    getAudioLevel,
    audioCache: cacheRef.current,
    restartVoice,
    playbackSpeed: options.playbackSpeed ?? 1.0,
    onServerAudioChunk,
    onTTSStart,
    onTTSEnd,
  };
}
